import * as React from 'react';
import { Ellipsis, Plus, Search } from 'lucide-react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { t } from '@reos/i18n';
import { FieldRow, FormSection } from '@redhand/ui';
import {
  addPartyContact,
  addPartyTaxIdentifier,
  archiveParty,
  createParty,
  deletePartyContact,
  listParties,
  getParty,
  listPartyContacts,
  updateParty,
  updatePartyContact,
  type ArchivePartyResult,
  type PartyDto,
  type PartyContact,
} from '../lib/parties-api.js';
import { systemClock } from '@reos/config';
import { listCaseParticipants, listCases, type KanbanCase } from '../lib/cases-api.js';
import { listTasks, type Task } from '../lib/tasks-api.js';
import { isTaskOpen } from '../lib/task-board.js';
import { RecordActivityButton } from '../components/record-activity-button.js';
import { listCalendarEvents } from '../lib/calendar-api.js';
import { clientActivityByParty, type ClientActivity } from '../lib/client-activity.js';
import { formatRelativeChange } from '../lib/relative-time.js';
import { CASE_NAME_TEXTS, caseDisplayName, isInternalIdTitle } from '../lib/case-title.js';
import { fetchPropertyAddresses } from '../lib/properties.js';
import { caseRoleCaption } from '../components/case-aside.js';
import type { AresCompanyDto } from '../lib/billing-api.js';
import { listInvoices, type InvoiceDto } from '../lib/invoices-api.js';
import { listContracts, type ContractDto } from '../lib/contracts-api.js';
import { DomainApiError } from '../lib/api-error.js';
import { humanApiError } from '../lib/form-errors.js';
import { stageBadgeClassA2, stageLabel } from '../lib/case-stages.js';
import { InvoiceStatusBadge } from '../components/invoices.js';
import { PageHead } from '../components/page-head.js';
import { FormLayout, FieldGrid } from '../components/form-layout.js';
import { IcoAresField } from '../components/ico-ares-field.js';

const cs = { locale: 'cs' } as const;

/**
 * Texty relativniho casu. Krok SEZ1: jedna kopie pro seznam (posledni
 * kontakt / dalsi akce) i pro kartu zapisu v detailu klienta — dve by se
 * rozesly.
 */
const RELATIVE_TEXTS = {
  today: (time: string) => t('webAdmin.relativeTime.today', { ...cs, values: { time } }),
  yesterday: (time: string) => t('webAdmin.relativeTime.yesterday', { ...cs, values: { time } }),
};

const KIND_LABELS: Readonly<Record<PartyDto['kind'], string>> = {
  person: t('webAdmin.klienti.kindPerson', cs),
  organization: t('webAdmin.klienti.kindOrganization', cs),
};

const STATUS_LABELS: Readonly<Record<PartyDto['status'], string>> = {
  active: t('webAdmin.klienti.statusActive', cs),
  archived: t('webAdmin.klienti.statusArchived', cs),
  merged: t('webAdmin.klienti.statusMerged', cs),
};

const CONTRACT_STATUS_LABELS: Readonly<Record<ContractDto['status'], string>> = {
  draft: t('webAdmin.contracts.status.draft', cs),
  ready_to_send: t('webAdmin.contracts.status.ready_to_send', cs),
  awaiting_signature: t('webAdmin.contracts.status.awaiting_signature', cs),
  signed: t('webAdmin.contracts.status.signed', cs),
  archived: t('webAdmin.contracts.status.archived', cs),
};

/** Kolik klientu nacte jedna stranka. Tentyz pocet se dohledava kontakty. */
const PAGE_LIMIT = 20;
/** Krok SEZ1: jedna stranka zapisu a ukolu klienta (karta „Zápisy a úkoly"). */
const RECORDS_PAGE = 100;

/**
 * Krok UXF2 bod 4 (nalez #18) — KDO TEN CLOVEK JE, UZ V SEZNAMU.
 *
 * Role a zakazka nejsou na `party` (role patri VAZBE klient–zakazka), proto
 * se dohledavaji z `GET /v1/cases?participantPartyId=` a
 * `GET /v1/cases/:id/participants`. Je to jen pro JEDNU stranku seznamu (20
 * klientu) a pri novem hledani se dotazy prerusi — tentyz strop, jaky uz plati
 * pro telefon (ROZHODNUTI UXR2-7 vyse).
 */
interface ClientCaseLink {
  readonly caseId: string;
  readonly role: string | null;
}

/**
 * `/klienti` — adresar zakazniku nad kontextem `party` (zadani REOS-R4 bod 4),
 * od kroku UXR2 podle SCHVALENEHO mockupu `04-klienti.html`: seznam
 * s hledanim vlevo, detail vybraneho klienta vpravo (kontakt, zakazky,
 * smlouvy, faktury).
 *
 * Jeden zdroj pravdy — `@reos/party` (`/v1/parties*`), zadna druha tabulka
 * klientu. Vazba na zakazky pres `GET /v1/cases?participantPartyId=`.
 *
 * ROZHODNUTI UXR2-7: sloupec „Telefon" se dohledava po jednom
 * (`/v1/parties/:id/contacts`) — davkovy endpoint na kontakty neexistuje.
 * Je to proto omezene na JEDNU STRANKU seznamu (20 klientu) a dotazy se pri
 * novem hledani prerusi. Sloupec „Zakázky" z predlohy by znamenal dalsich
 * 20 dotazu pri kazdem psani, takze zakazky zustavaji v detailu klienta —
 * tam, kde se s nimi stejne pracuje.
 */
export function KlientiPage(): React.JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { klient?: string };
  const selectedId = search.klient ?? null;
  const [parties, setParties] = React.useState<PartyDto[] | null>(null);
  const [total, setTotal] = React.useState<number | null>(null);
  const [phones, setPhones] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [emails, setEmails] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [links, setLinks] = React.useState<ReadonlyMap<string, ClientCaseLink>>(new Map());
  /**
   * Nazvy zakazek pro sloupec „Zakázka" — ze SSOT `caseDisplayName` (krok
   * O8/D1), aby v radku nikdy neskoncilo interni id.
   *
   * Krok APP2 bod 1: nazev se sklada z RADKU, ktery uz seznam ma
   * (`GET /v1/cases/by-participants`). Do APP2 se na tenhle jeden sloupec
   * stahoval cely seznam zakazek kancelare (`fetchCaseAddressIndex`).
   */
  const [caseNames, setCaseNames] = React.useState<ReadonlyMap<string, string>>(new Map());
  /**
   * Krok UXF5 bod 6 (nalez A7 / #18): „Poslední kontakt" a „Další akce".
   * JEDNA davka ukolu a JEDNA davka udalosti na obrazovku — ne dotaz na
   * radek (to uz dela jen telefon, viz ROZHODNUTI UXR2-7). Vypocet je
   * v `lib/client-activity.ts`.
   */
  const [activity, setActivity] = React.useState<ReadonlyMap<string, ClientActivity>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [refreshToken, setRefreshToken] = React.useState(0);
  const [showNewForm, setShowNewForm] = React.useState(false);

  const select = React.useCallback(
    (partyId: string | null): void => {
      void navigate({
        to: '.',
        search: partyId === null ? {} : { klient: partyId },
        resetScroll: false,
      });
    },
    [navigate],
  );

  React.useEffect(() => {
    let alive = true;
    void Promise.all([
      listTasks({ limit: 200 }).then((page) => page.items).catch(() => []),
      listCalendarEvents({ limit: 500 }).catch(() => []),
    ]).then(([tasks, events]) => {
      if (!alive) return;
      setActivity(clientActivityByParty({ tasks, events, now: systemClock.now() }));
    });
    return () => {
      alive = false;
    };
  }, [refreshToken]);

  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await listParties({
            limit: PAGE_LIMIT,
            ...(query.trim() !== '' ? { q: query.trim() } : {}),
          });
          if (controller.signal.aborted) return;
          setParties(result.items);
          // Pocet „z kolika" zna jen dalsi stranka — bez nej se ukaze aspon
          // to, co je nacteno (nikdy vymyslene cislo).
          setTotal(result.nextCursor === null ? result.items.length : null);
          setPhones(new Map());
          setEmails(new Map());
          setLinks(new Map());
          const found = new Map<string, string>();
          const foundEmails = new Map<string, string>();
          const foundLinks = new Map<string, ClientCaseLink>();
          const foundNames = new Map<string, string>();
          /** Zakazky, ktere seznamu vysly jako „ta, kterou s klientem resim". */
          const linked: KanbanCase[] = [];
          await Promise.all(
            result.items.map(async (party) => {
              const [contacts, cases] = await Promise.all([
                listPartyContacts(party.id).catch(() => []),
                listCases({ participantPartyId: party.id, limit: 1 })
                  .then((page) => page.items)
                  .catch(() => []),
              ]);
              const phone = contacts.find((contact) => contact.kind === 'phone');
              if (phone !== undefined) found.set(party.id, phone.value);
              const email = contacts.find((contact) => contact.kind === 'email');
              if (email !== undefined) foundEmails.set(party.id, email.value);
              const first = cases[0];
              if (first !== undefined) {
                /*
                 * Krok APP2 bod 1: role chodi ROVNOU S VAZBOU
                 * (`participantRole` z `GET /v1/cases/by-participants`) — je to
                 * atribut vazby klient–zakazka. Dotaz na ucastniky zbyva jen
                 * pro zakazku, ktera neprisla dotazem na stranu; do APP2 byl na
                 * KAZDY radek a delal z dvaceti radku ctyricet dotazu.
                 */
                const role =
                  first.participantRole ??
                  (await listCaseParticipants(first.id)
                    .then((participants) => participants.find((p) => p.partyId === party.id)?.role ?? null)
                    .catch(() => null));
                foundLinks.set(party.id, { caseId: first.id, role });
                linked.push(first);
              }
            }),
          );
          /*
           * Nazev zakazky sklada TATAZ funkce jako index i hledani (SSOT
           * `caseDisplayName`), takze v radku nikdy neskonci interni id.
           * Adresa nemovitosti se dohledava JEN pro zakazky se zastupnym
           * nazvem — zakazka zalozena pruvodcem ma v nazvu adresu, takze
           * obvykle to je nula dotazu. Do APP2 se na tenhle jeden sloupec
           * stahoval CELY seznam zakazek kancelare (343 zakazek MV = ctyri
           * dotazy navic, v DEMO kancelari kvota 429).
           */
          const bezNazvu = linked.filter((c) => isInternalIdTitle(c.title) && c.propertyId !== null);
          const addressByProperty =
            bezNazvu.length === 0
              ? new Map<string, string>()
              : await fetchPropertyAddresses(
                  bezNazvu.map((c) => c.propertyId).filter((id): id is string => id !== null),
                ).catch(() => new Map<string, string>());
          for (const item of linked) {
            foundNames.set(item.id, caseDisplayName(item, addressByProperty, CASE_NAME_TEXTS).text);
          }
          if (!controller.signal.aborted) {
            setPhones(found);
            setEmails(foundEmails);
            setLinks(foundLinks);
            setCaseNames(foundNames);
          }
        } catch (e) {
          if (controller.signal.aborted) return;
          setError(e instanceof DomainApiError ? e.message : t('webAdmin.klienti.loadError', cs));
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      })();
    }, 250);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query, refreshToken]);

  const items = parties ?? [];
  /** Tytez retezce jako `/zakazky` — „dnes 9:40" / „29. 9." (SSOT `relative-time.ts`). */
  const now = React.useMemo(() => systemClock.now(), [activity]);
  return (
    <div data-testid="klienti-page">
      <PageHead
        title={t('webAdmin.klienti.title', cs)}
        subtitle={
          parties === null
            ? undefined
            : t('webAdmin.klientiV2.subtitle', { ...cs, values: { count: items.length } })
        }
        action={
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--primary"
            onClick={() => setShowNewForm((v) => !v)}
            data-testid="klienti-new-button"
          >
            <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
              <Plus size={15} strokeWidth={2} />
            </span>
            {t('webAdmin.klienti.newButton', cs)}
          </button>
        }
      />

      <div className="rh-a2-page rh-a2-page--wide">
        {showNewForm && (
          <NewClientForm
            onCreated={(party) => {
              setShowNewForm(false);
              setRefreshToken((v) => v + 1);
              select(party.id);
            }}
            onCancel={() => setShowNewForm(false)}
          />
        )}

        {/*
          Krok VZH1 (doplneni R.B. 6. 10. bod 6): dokud neni klient vybrany,
          NENI vedle seznamu prazdna karta „Vyberte klienta v seznamu vlevo" —
          seznam bere celou sirku a detail se otevre jako bocni panel teprve
          po vyberu. Prazdna karta vedle plneho seznamu nic nerika a brala
          tretinu obrazovky.
        */}
        <div className={`rh-a2-cols${selectedId === null ? '' : ' rh-a2-cols--main'}`}>
          <div>
            <div className="rh-a2-filters">
              {/* Hledani s lupou jako v predloze (`.search` v `04-klienti.html`). */}
              <div className="rh-a2-search" style={{ maxWidth: 320 }}>
                <span className="rh-a2-ico rh-a2-ico--sm rh-a2-search__icon" aria-hidden="true">
                  <Search size={15} strokeWidth={1.75} />
                </span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('webAdmin.klienti.searchPlaceholder', cs)}
                  aria-label={t('webAdmin.klienti.searchAriaLabel', cs)}
                  data-testid="klienti-search"
                />
              </div>
            </div>

            {error !== null && (
              <p role="alert" className="rh-a2-error" data-testid="klienti-error">
                {error}
              </p>
            )}
            {loading && parties === null && <p role="status" className="rh-a2-status">{t('webAdmin.klienti.loading', cs)}</p>}

            {parties !== null && items.length === 0 ? (
              <div className="rh-a2-empty" data-testid="klienti-empty">
                <p className="rh-a2-empty__title">{t('webAdmin.klienti.emptyTitle', cs)}</p>
                <p style={{ margin: 0 }}>{t('webAdmin.klienti.emptyDescription', cs)}</p>
              </div>
            ) : null}

            {items.length > 0 && (
              <>
                <div className="rh-a2-tbl-wrap">
                  <table className="rh-a2-tbl" data-testid="klienti-list">
                    {/*
                      Krok UXF2 bod 4 (nalez #18): radek rika KDO ten clovek
                      je (role ve zakazce), JAK se mu ozvat (e-mail, telefon)
                      a KTEROU zakazku s nim resim. Vse z dat, ktera uz API
                      ma — zadny novy endpoint.
                    */}
                    <thead>
                      <tr>
                        <th style={{ width: '28%' }}>{t('webAdmin.klientiV2.columnName', cs)}</th>
                        <th>{t('webAdmin.klientiV2.columnRole', cs)}</th>
                        <th>{t('webAdmin.klientiV2.columnEmail', cs)}</th>
                        <th>{t('webAdmin.klientiV2.columnPhone', cs)}</th>
                        <th>{t('webAdmin.klientiV2.columnCase', cs)}</th>
                        {/* Krok UXF5 bod 6 (nalez A7): komu zavolat se musi
                            poznat ZE SEZNAMU, bez otevirani kazdeho klienta. */}
                        <th className="rh-a2-nowrap">{t('webAdmin.klientiV2.columnLastContact', cs)}</th>
                        <th>{t('webAdmin.klientiV2.columnNextAction', cs)}</th>
                        <th>{t('webAdmin.klientiV2.columnStatus', cs)}</th>
                        <th>
                          <span className="rh-a2-sr-only">{t('webAdmin.zakazky.rowMenuLabel', cs)}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((party) => (
                        <tr key={party.id} data-testid={`klient-row-${party.id}`} data-selected={String(selectedId === party.id)}>
                          <td data-label={t('webAdmin.klientiV2.columnName', cs)}>
                            <button
                              type="button"
                              className="rh-a2-strong"
                              onClick={() => select(party.id)}
                              data-testid={`klient-${party.id}`}
                            >
                              {party.displayName}
                            </button>
                            <span className="rh-a2-sub">
                              {KIND_LABELS[party.kind]}
                              {party.legalName !== null && party.legalName !== party.displayName
                                ? ` · ${party.legalName}`
                                : ''}
                            </span>
                          </td>
                          <td data-label={t('webAdmin.klientiV2.columnRole', cs)}>
                            {(() => {
                              const role = links.get(party.id)?.role ?? null;
                              return role === null ? null : (
                                <span className="rh-a2-badge" data-testid={`klient-role-${party.id}`}>
                                  {caseRoleCaption(role)}
                                </span>
                              );
                            })()}
                          </td>
                          <td data-label={t('webAdmin.klientiV2.columnEmail', cs)}>
                            {emails.get(party.id) === undefined ? null : (
                              <a href={`mailto:${emails.get(party.id) ?? ''}`} data-testid={`klient-email-${party.id}`}>
                                {emails.get(party.id)}
                              </a>
                            )}
                          </td>
                          <td className="rh-a2-nowrap rh-a2-num" data-label={t('webAdmin.klientiV2.columnPhone', cs)}>
                            {/* Krok UXP2 (P2-12): telefon jako `tel:` odkaz — na mobilu jde klepnutim zavolat. */}
                            {(() => {
                              const phone = phones.get(party.id);
                              return phone === undefined ? (
                                <span className="rh-a2-muted">{t('webAdmin.kanban.noValue', cs)}</span>
                              ) : (
                                <a href={`tel:${phone.replace(/\s+/gu, '')}`} data-testid={`klient-phone-${party.id}`}>
                                  {phone}
                                </a>
                              );
                            })()}
                          </td>
                          <td data-label={t('webAdmin.klientiV2.columnCase', cs)}>
                            {(() => {
                              const link = links.get(party.id);
                              if (link === undefined) return null;
                              return (
                                <Link
                                  to="/zakazky/$id"
                                  params={{ id: link.caseId }}
                                  data-testid={`klient-case-${party.id}`}
                                >
                                  {caseNames.get(link.caseId) ?? t('webAdmin.klientiV2.columnCase', cs)}
                                </Link>
                              );
                            })()}
                          </td>
                          <td className="rh-a2-nowrap" data-label={t('webAdmin.klientiV2.columnLastContact', cs)}>
                            {(() => {
                              const last = activity.get(party.id)?.lastContactAt ?? null;
                              return last === null ? (
                                <span className="rh-a2-muted" data-testid={`klient-last-contact-${party.id}`}>
                                  {t('webAdmin.klientiV2.lastContactNever', cs)}
                                </span>
                              ) : (
                                <span data-testid={`klient-last-contact-${party.id}`}>
                                  {formatRelativeChange(last, now, RELATIVE_TEXTS)}
                                </span>
                              );
                            })()}
                          </td>
                          <td data-label={t('webAdmin.klientiV2.columnNextAction', cs)}>
                            {(() => {
                              const next = activity.get(party.id)?.nextAction ?? null;
                              return next === null ? (
                                <span className="rh-a2-muted" data-testid={`klient-next-action-${party.id}`}>
                                  {t('webAdmin.klientiV2.nextActionNone', cs)}
                                </span>
                              ) : (
                                <span data-testid={`klient-next-action-${party.id}`}>
                                  {next.title}
                                  <span className="rh-a2-sub">{formatRelativeChange(next.at, now, RELATIVE_TEXTS)}</span>
                                </span>
                              );
                            })()}
                          </td>
                          <td data-label={t('webAdmin.klientiV2.columnStatus', cs)}>
                            <span className={`rh-a2-badge ${party.status === 'active' ? 'rh-a2-badge--ok' : ''}`}>
                              {STATUS_LABELS[party.status]}
                            </span>
                          </td>
                          <td>
                            <div className="rh-a2-rowmenu">
                              <button
                                type="button"
                                className="rh-a2-dots"
                                aria-label={t('webAdmin.zakazky.rowMenuLabel', cs)}
                                onClick={() => select(party.id)}
                                data-testid={`klient-row-menu-${party.id}`}
                              >
                                <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                                  <Ellipsis size={15} strokeWidth={1.75} />
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="rh-a2-meta" style={{ marginTop: 12 }} data-testid="klienti-count">
                  {t('webAdmin.klientiV2.shownOf', {
                    ...cs,
                    values: { shown: items.length, total: total ?? `${String(items.length)}+` },
                  })}
                </p>
              </>
            )}
          </div>

          {selectedId === null ? null : (
            <div className="rh-a2-stack">
              <KlientDetail partyId={selectedId} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Facts({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return <ul className="rh-a2-facts">{children}</ul>;
}

function Fact({ label, children }: { readonly label: string; readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <li>
      <span className="rh-a2-k">{label}</span>
      <span className="rh-a2-v">{children}</span>
    </li>
  );
}


/**
 * Krok PROP7 bod 2 — OPRAVA UDAJU KLIENTA NA OBRAZOVCE (nalez 20).
 *
 * Do PROP7 slo v adresari kontakt jen PRIDAT, takze prekliknuty telefon
 * zustal navzdy a makler k nemu pridal druhy radek. Formular opravuje TYZ
 * radek (`updatePartyContact`), prazdna hodnota ho smaze a nova hodnota
 * u chybejiciho druhu ho zalozi — jedna obrazovka pro vsechny tri pohyby.
 *
 * Stav se plni z props PRI RENDERU (ne v `useEffect`): efekt po pripojeni
 * je zavod, ktery pada jen pod plnou branou (poučeni UXV4b).
 */
interface ContactDraft {
  readonly name: string;
  readonly email: string;
  readonly phone: string;
  readonly address: string;
}

function draftOf(party: PartyDto, contacts: readonly PartyContact[]): ContactDraft {
  const value = (kind: PartyContact['kind']): string =>
    contacts.find((contact) => contact.kind === kind)?.value ?? '';
  return {
    name: party.displayName,
    email: value('email'),
    phone: value('phone'),
    address: value('address'),
  };
}

/** Lidsky nazev vazby do vety „visí na …" (jeden zdroj pro oba stavy). */
const LINK_LABELS: Readonly<Record<ArchivePartyResult['linkedTo'][number], string>> = {
  contract: t('webAdmin.klienti.linkContract', cs),
  case: t('webAdmin.klienti.linkCase', cs),
  invoice: t('webAdmin.klienti.linkInvoice', cs),
};

function KlientContactsForm({
  party,
  contacts,
  onSaved,
  onCancel,
}: {
  readonly party: PartyDto;
  readonly contacts: readonly PartyContact[];
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [form, setForm] = React.useState<ContactDraft>(() => draftOf(party, contacts));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const set = (patch: Partial<ContactDraft>): void => setForm((prev) => ({ ...prev, ...patch }));

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (form.name.trim() !== '' && form.name.trim() !== party.displayName) {
        await updateParty(party.id, { displayName: form.name.trim() });
      }
      for (const kind of ['email', 'phone', 'address'] as const) {
        const next = form[kind].trim();
        const existing = contacts.find((contact) => contact.kind === kind);
        if (existing === undefined) {
          if (next !== '') await addPartyContact(party.id, { kind, value: next, makePrimary: true });
          continue;
        }
        if (next === '') {
          await deletePartyContact(party.id, existing.id);
          continue;
        }
        // Nezmenenou hodnotu neposilame — jinak by audit mel zapis bez zmeny.
        if (next !== existing.value) {
          await updatePartyContact(party.id, existing.id, { value: next });
        }
      }
      onSaved();
    } catch (e) {
      setError(humanApiError(e, 'webAdmin.klienti.contactSaveError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} data-testid="klient-contacts-form">
      <FormSection title={t('webAdmin.klienti.editContactsTitle', cs)}>
        <FieldRow label={t('webAdmin.klienti.nameLabel', cs)}>
          <input
            className="rh-a2-input"
            data-testid="klient-edit-name"
            value={form.name}
            onChange={(event) => set({ name: event.target.value })}
          />
        </FieldRow>
        <FieldRow label={t('webAdmin.klienti.phoneLabel', cs)}>
          <input
            className="rh-a2-input"
            data-testid="klient-edit-phone"
            value={form.phone}
            onChange={(event) => set({ phone: event.target.value })}
          />
        </FieldRow>
        <FieldRow label={t('webAdmin.klienti.emailLabel', cs)}>
          <input
            className="rh-a2-input"
            data-testid="klient-edit-email"
            value={form.email}
            onChange={(event) => set({ email: event.target.value })}
          />
        </FieldRow>
        <FieldRow label={t('webAdmin.klienti.addressLabel', cs)}>
          <input
            className="rh-a2-input"
            data-testid="klient-edit-address"
            value={form.address}
            onChange={(event) => set({ address: event.target.value })}
          />
        </FieldRow>
      </FormSection>
      {error !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="klient-contacts-error">
          {error}
        </p>
      ) : null}
      <div className="rh-a2-actions">
        <button type="button" className="rh-a2-btn" onClick={onCancel} data-testid="klient-contacts-cancel">
          {t('webAdmin.klienti.cancelButton', cs)}
        </button>
        <button
          type="submit"
          className="rh-a2-btn rh-a2-btn--primary"
          disabled={saving}
          data-testid="klient-contacts-save"
        >
          {t('webAdmin.klienti.saveButton', cs)}
        </button>
      </div>
    </form>
  );
}

/** Detail klienta podle predlohy: kontakt, zakazky, smlouvy, faktury. */
function KlientDetail({ partyId }: { readonly partyId: string }): React.JSX.Element {
  const [party, setParty] = React.useState<PartyDto | null>(null);
  const [contacts, setContacts] = React.useState<PartyContact[] | null>(null);
  const [cases, setCases] = React.useState<KanbanCase[] | null>(null);
  const [contracts, setContracts] = React.useState<ContractDto[] | null>(null);
  const [invoices, setInvoices] = React.useState<InvoiceDto[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  /** Krok PROP7 bod 2: oprava udaju a odebrani osoby z adresare. */
  const [editing, setEditing] = React.useState(false);
  const [removal, setRemoval] = React.useState<ArchivePartyResult | null>(null);
  const [removeError, setRemoveError] = React.useState<string | null>(null);
  const [reloadToken, setReloadToken] = React.useState(0);
  /**
   * Krok SEZ1 bod 2: zapisy a ukoly klienta — tytez `crm_tasks`, ze kterych
   * seznam uz pocita „posledni kontakt" a „dalsi akce"
   * (`lib/client-activity.ts`). Zadny druhy model historie.
   */
  const [records, setRecords] = React.useState<readonly Task[] | null>(null);
  /**
   * Krok SEZ1: server neumi radit „nejnovejsi prvni" (radi podle terminu),
   * takze karta cte jednu stranku a radi ji sama. Kdyz ma klient zapisu VIC
   * nez stranku, karta to RIKÁ — misto aby tise tvrdila, ze zadne dalsi
   * nejsou.
   */
  const [recordsTruncated, setRecordsTruncated] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setRecords(null);
    setRecordsTruncated(false);
    void listTasks({ partyId, limit: RECORDS_PAGE })
      .then((result) => {
        /*
         * Server radi ukoly PODLE TERMINU (`due_at asc`, bez terminu az
         * nakonec) — to je poradi pro tabuli, ne pro historii. Karta chce
         * NEJNOVEJSI NAHORE, tedy podle toho, kdy se co stalo nebo ma stat;
         * stejne jako „Poslední dění" u zakazky (`caseEventLines`).
         */
        if (!cancelled) {
          setRecords(
            [...result.items].sort((a, b) =>
              (b.completedAt ?? b.dueAt ?? b.createdAt).localeCompare(a.completedAt ?? a.dueAt ?? a.createdAt),
            ),
          );
          setRecordsTruncated(result.nextCursor !== null);
        }
      })
      // Zapisy jsou samostatny dotaz: role bez pristupu k ukolum nesmi
      // shodit zbytek detailu klienta (stejne jako doklady niz).
      .catch(() => {
        if (!cancelled) setRecords([]);
      });
    return () => {
      cancelled = true;
    };
  }, [partyId, reloadToken]);

  /**
   * Vycisteni obrazovky patri ZMENE KLIENTA, ne obnoveni tehoz klienta.
   *
   * Oprava e2e 1 kroku KAT1l2 (S38b, desktop i mobil: `zapsat-saved`
   * „element(s) not found"): po ulozeni zapisu se detail obnovi
   * (`reloadToken`). Dokud nulovani `party` bezelo i pri obnoveni, vratil se
   * detail na „Načítá se…", cimz se ODMONTOVALA cela karta zapisu — a s ni
   * potvrzeni „Zapsáno: …", ktere drzi `RecordActivityButton` ve svem stavu.
   * Makler tak po kliknuti na „Uložit" nedostal zadnou odpoved.
   */
  React.useEffect(() => {
    setParty(null);
    setContacts(null);
    setCases(null);
    setContracts(null);
    setInvoices(null);
  }, [partyId]);

  React.useEffect(() => {
    let cancelled = false;
    setError(null);
    void (async () => {
      try {
        const [p, c, list] = await Promise.all([
          getParty(partyId),
          listPartyContacts(partyId),
          listCases({ participantPartyId: partyId, limit: 25 }),
        ]);
        if (cancelled) return;
        setParty(p);
        setContacts(c);
        setCases(list.items);
        // Smlouvy se vazou na ZAKAZKU, ne na stranu — API filtr podle strany
        // neexistuje, takze se ptame na zakazky klienta (jednotky, ne stovky).
        const perCase = await Promise.all(
          list.items.slice(0, 10).map((item) => listContracts({ caseId: item.id }).catch(() => [])),
        );
        if (!cancelled) setContracts(perCase.flat());
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof DomainApiError ? e.message : t('webAdmin.klienti.detailLoadError', cs));
      }
      // Doklady jsou samostatny dotaz - role bez pristupu k fakturaci nesmi
      // shodit zbytek detailu klienta (viz `finance.client_invoice.manage*`).
      try {
        const items = await listInvoices({ partyId });
        if (!cancelled) setInvoices(items);
      } catch {
        if (!cancelled) setInvoices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [partyId, reloadToken]);

  async function removeFromDirectory(): Promise<void> {
    setRemoveError(null);
    try {
      setRemoval(await archiveParty(partyId));
      setReloadToken((token) => token + 1);
    } catch (e) {
      setRemoveError(humanApiError(e, 'webAdmin.klienti.removeClientError'));
    }
  }

  if (error !== null) {
    return (
      <section className="rh-a2-card">
        <p role="alert" className="rh-a2-error" style={{ margin: 0 }} data-testid="klient-detail-error">
          {error}
        </p>
      </section>
    );
  }
  if (party === null) {
    return (
      <section className="rh-a2-card">
        <p role="status" style={{ margin: 0 }}>{t('webAdmin.klienti.loadingShort', cs)}</p>
      </section>
    );
  }

  const email = contacts?.find((contact) => contact.kind === 'email')?.value ?? null;
  const phone = contacts?.find((contact) => contact.kind === 'phone')?.value ?? null;
  const address = contacts?.find((contact) => contact.kind === 'address')?.value ?? null;

  return (
    <div data-testid="klient-detail">
      <section className="rh-a2-card">
        <div className="rh-a2-card__head">
          <h2>{party.displayName}</h2>
        </div>
        <p className="rh-a2-card__hint">
          {KIND_LABELS[party.kind]} · {STATUS_LABELS[party.status]}
        </p>
        {editing && contacts !== null ? (
          <KlientContactsForm
            party={party}
            contacts={contacts}
            onSaved={() => {
              setEditing(false);
              setReloadToken((token) => token + 1);
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <Facts>
            <Fact label={t('webAdmin.klienti.phoneLabel', cs)}>
              {phone ?? <span className="rh-a2-muted">{t('webAdmin.klienti.noContacts', cs)}</span>}
            </Fact>
            <Fact label={t('webAdmin.klienti.emailLabel', cs)}>
              {email ?? <span className="rh-a2-muted">{t('webAdmin.klienti.noContacts', cs)}</span>}
            </Fact>
            {address !== null ? <Fact label={t('webAdmin.klienti.addressLabel', cs)}>{address}</Fact> : null}
          </Facts>
        )}
        {editing ? null : (
          <div className="rh-a2-actions">
            <button
              type="button"
              className="rh-a2-btn"
              onClick={() => setEditing(true)}
              disabled={contacts === null}
              data-testid="klient-edit-contacts"
            >
              {t('webAdmin.klienti.editContactsButton', cs)}
            </button>
            <button
              type="button"
              className="rh-a2-btn"
              onClick={() => {
                // Potvrzeni je zamerne pred akci: osoba bez vazeb z adresare
                // zmizi uplne a makler to ma vedet PREDEM.
                if (globalThis.confirm(t('webAdmin.klienti.removeClientConfirm', cs))) {
                  void removeFromDirectory();
                }
              }}
              disabled={party.status !== 'active'}
              data-testid="klient-remove"
            >
              {t('webAdmin.klienti.removeClientButton', cs)}
            </button>
          </div>
        )}
        {removal !== null ? (
          <p role="status" className="rh-a2-meta" data-testid="klient-remove-result">
            {removal.linkedTo.length === 0
              ? t('webAdmin.klienti.removedTitle', cs)
              : t('webAdmin.klienti.archivedTitle', {
                  ...cs,
                  values: { links: removal.linkedTo.map((link) => LINK_LABELS[link]).join(', ') },
                })}
          </p>
        ) : null}
        {removeError !== null ? (
          <p role="alert" className="rh-a2-error" data-testid="klient-remove-error">
            {removeError}
          </p>
        ) : null}
      </section>

      {/*
        Krok SEZ1 bod 2 (R.B. 29. 10., Realman bod 9): „Zapsat" stoji HNED
        pod kontaktem — je to ta akce, kterou makler po telefonu dela
        nejcasteji, a na mobilu 390 na ni nesmi muset scrollovat. Pod
        tlacitkem je tataz historie, do ktere zapis spadne, aby bylo videt,
        ze se opravdu ulozil.
      */}
      <section className="rh-a2-card" data-testid="klient-zapisy">
        <div className="rh-a2-card__head">
          <h2>{t('webAdmin.recordActivity.historyHeading', cs)}</h2>
          {records !== null ? <span className="rh-a2-badge">{records.length}</span> : null}
        </div>
        <div className="rh-a2-actions" style={{ marginBottom: 8 }}>
          <RecordActivityButton
            target={{ kind: 'party', partyId }}
            onRecorded={() => setReloadToken((token) => token + 1)}
          />
        </div>
        {records === null ? (
          <p style={{ margin: 0 }}>{t('webAdmin.klienti.loadingShort', cs)}</p>
        ) : records.length === 0 ? (
          <p className="rh-a2-muted" style={{ margin: 0 }} data-testid="klient-zapisy-empty">
            {t('webAdmin.recordActivity.historyEmpty', cs)}
          </p>
        ) : (
          <ul className="rh-a2-rows" data-testid="klient-zapisy-list">
            {records.map((record) => (
              <li key={record.id}>
                <span className="rh-a2-grow">
                  <span className="rh-a2-title">{record.title}</span>
                  <span className="rh-a2-meta">
                    {formatRelativeChange(
                      record.completedAt ?? record.dueAt ?? record.createdAt,
                      systemClock.now(),
                      RELATIVE_TEXTS,
                    )}
                  </span>
                </span>
                <span className="rh-a2-badge">
                  {isTaskOpen(record.status)
                    ? t('webAdmin.recordActivity.historyOpen', cs)
                    : t('webAdmin.recordActivity.historyDone', cs)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {recordsTruncated ? (
          <p className="rh-a2-meta" style={{ marginBottom: 0 }} data-testid="klient-zapisy-strop">
            {t('webAdmin.recordActivity.historyTruncated', { ...cs, values: { count: RECORDS_PAGE } })}
          </p>
        ) : null}
      </section>

      <section className="rh-a2-card" data-testid="klient-cases">
        <div className="rh-a2-card__head">
          <h2>{t('webAdmin.klienti.casesHeading', cs)}</h2>
          {cases !== null ? <span className="rh-a2-badge">{cases.length}</span> : null}
        </div>
        {cases === null ? (
          <p style={{ margin: 0 }}>{t('webAdmin.klienti.loadingShort', cs)}</p>
        ) : cases.length === 0 ? (
          <p className="rh-a2-muted" style={{ margin: 0 }}>{t('webAdmin.klienti.noCases', cs)}</p>
        ) : (
          <ul className="rh-a2-rows">
            {cases.map((item) => (
              <li key={item.id}>
                <span className="rh-a2-grow">
                  <span className="rh-a2-title">
                    <Link to="/zakazky/$id" params={{ id: item.id }}>
                      {item.title}
                    </Link>
                  </span>
                </span>
                <span className={`rh-a2-badge ${stageBadgeClassA2(item.stage)}`}>{stageLabel(item.stage)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rh-a2-card" data-testid="klient-contracts">
        <div className="rh-a2-card__head">
          <h2>{t('webAdmin.klientiV2.contractsHeading', cs)}</h2>
          {contracts !== null ? <span className="rh-a2-badge">{contracts.length}</span> : null}
        </div>
        {contracts === null ? (
          <p style={{ margin: 0 }}>{t('webAdmin.klienti.loadingShort', cs)}</p>
        ) : contracts.length === 0 ? (
          <p className="rh-a2-muted" style={{ margin: 0 }}>{t('webAdmin.klientiV2.noContracts', cs)}</p>
        ) : (
          <ul className="rh-a2-rows">
            {contracts.map((contract) => (
              <li key={contract.id}>
                <span
                  className={`rh-a2-dot ${contract.status === 'signed' ? 'rh-a2-dot--ok' : contract.status === 'awaiting_signature' ? 'rh-a2-dot--wait' : ''}`}
                  aria-hidden="true"
                />
                <span className="rh-a2-grow">
                  <span className="rh-a2-title">{contract.title}</span>
                  <span className="rh-a2-meta">{CONTRACT_STATUS_LABELS[contract.status]}</span>
                </span>
                <Link to="/smlouvy/$id" params={{ id: contract.id }} className="rh-a2-link">
                  {t('webAdmin.klientiV2.openContract', cs)}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rh-a2-card" data-testid="klient-invoices">
        <div className="rh-a2-card__head">
          <h2>{t('webAdmin.klienti.invoicesHeading', cs)}</h2>
          {invoices !== null ? <span className="rh-a2-badge">{invoices.length}</span> : null}
        </div>
        {invoices === null ? (
          <p style={{ margin: 0 }}>{t('webAdmin.klienti.loadingShort', cs)}</p>
        ) : invoices.length === 0 ? (
          <p className="rh-a2-muted" style={{ margin: 0 }}>{t('webAdmin.klienti.noInvoices', cs)}</p>
        ) : (
          <ul className="rh-a2-rows">
            {invoices.map((invoice) => (
              <li key={invoice.id}>
                <span className="rh-a2-grow">
                  <span className="rh-a2-title rh-a2-num">{invoice.myuctoNumber ?? invoice.issueDate}</span>
                  <span className="rh-a2-meta rh-a2-num">
                    {Number(invoice.totalAmount).toLocaleString('cs-CZ')} Kč
                  </span>
                </span>
                <InvoiceStatusBadge status={invoice.status} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ------------------------------------------------------------- novy klient

interface NewClientFormState {
  kind: PartyDto['kind'];
  displayName: string;
  ico: string;
  dic: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  zip: string;
}

const EMPTY_NEW_CLIENT: NewClientFormState = {
  kind: 'person',
  displayName: '',
  ico: '',
  dic: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  zip: '',
};

function NewClientForm({
  onCreated,
  onCancel,
}: {
  readonly onCreated: (party: PartyDto) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [form, setForm] = React.useState<NewClientFormState>(EMPTY_NEW_CLIENT);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  function applyAresCompany(company: AresCompanyDto): void {
    setForm((prev) => ({
      ...prev,
      displayName: company.companyName,
      street: company.street,
      city: company.city,
      zip: company.zip,
      ...(company.dic !== null ? { dic: company.dic } : {}),
    }));
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const address = [form.street.trim(), `${form.zip.trim()} ${form.city.trim()}`.trim()].filter((v) => v !== '').join(', ');
      const contacts: { kind: 'email' | 'phone' | 'address'; value: string }[] = [];
      if (form.email.trim() !== '') contacts.push({ kind: 'email', value: form.email.trim() });
      if (form.phone.trim() !== '') contacts.push({ kind: 'phone', value: form.phone.trim() });
      if (address !== '') contacts.push({ kind: 'address', value: address.slice(0, 100) });
      const { party } = await createParty({
        kind: form.kind,
        displayName: form.displayName.trim(),
        ...(contacts.length > 0 ? { contacts } : {}),
      });
      if (form.kind === 'organization' && form.ico.trim() !== '') {
        await addPartyTaxIdentifier(party.id, { country: 'CZ', type: 'CRN', value: form.ico.trim() });
      }
      if (form.kind === 'organization' && form.dic.trim() !== '') {
        await addPartyTaxIdentifier(party.id, { country: 'CZ', type: 'VAT', value: form.dic.trim() });
      }
      setForm(EMPTY_NEW_CLIENT);
      onCreated(party);
    } catch (e) {
      // Krok SML5 bod 3: znamy duvod (`type`) dostane vetu, ktera rika co delat.
      setError(humanApiError(e, 'webAdmin.klienti.createError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="mb-4" data-testid="klienti-new-form">
      <FormLayout
        width="narrow"
        actions={
          <>
            <button type="button" className="rh-a2-btn" onClick={onCancel} data-testid="klienti-new-cancel">
              {t('webAdmin.klienti.cancelButton', cs)}
            </button>
            <span className="rh-a2-formbar__spacer" />
            <button type="submit" className="rh-a2-btn rh-a2-btn--primary" disabled={saving || form.displayName.trim() === ''} data-testid="klienti-new-submit">
              {t('webAdmin.klienti.saveButton', cs)}
            </button>
          </>
        }
        main={
      <FormSection title={t('webAdmin.klienti.newFormTitle', cs)}>
        {error !== null && (
          <p role="alert" className="rh-a2-error">
            {error}
          </p>
        )}
        <div className="rh-a2-toggle" role="radiogroup" aria-label={t('webAdmin.klienti.kindLabel', cs)}>
          <button
            type="button"
            className={`rh-a2-toggle__btn${form.kind === 'person' ? ' is-on' : ''}`}
            aria-pressed={form.kind === 'person'}
            onClick={() => setForm((prev) => ({ ...prev, kind: 'person' }))}
            data-testid="klienti-new-kind-person"
          >
            {t('webAdmin.klienti.kindPerson', cs)}
          </button>
          <button
            type="button"
            className={`rh-a2-toggle__btn${form.kind === 'organization' ? ' is-on' : ''}`}
            aria-pressed={form.kind === 'organization'}
            onClick={() => setForm((prev) => ({ ...prev, kind: 'organization' }))}
            data-testid="klienti-new-kind-organization"
          >
            {t('webAdmin.klienti.kindOrganization', cs)}
          </button>
        </div>

        {/*
          Krok UXF3 bod 2 (#19, N7): JEDEN tok poli pro osobu i firmu — jmeno
          a ulice na plnou sirku panelu, telefon a e-mail na poloviny (pod
          900px jeden sloupec, `.rh-a2-field-grid`). Firma nad tim navic IČO +
          DIČ; osoba danove identifikatory nevidi. Dve kopie poli (verze UXK2)
          se rozesly: u firmy stal telefon pod sebou a jmenoval se stejne jako
          u osoby, i kdyz to je nazev firmy.
        */}
        {form.kind === 'organization' && (
          <FieldGrid columns="2fr 1fr">
            <IcoAresField id="klienti-new-ico" label={t('webAdmin.klienti.icoLabel', cs)} value={form.ico} onChange={(ico) => setForm((prev) => ({ ...prev, ico }))} onFound={applyAresCompany} />
            <FieldRow label={t('webAdmin.klienti.dicLabel', cs)} htmlFor="klienti-new-dic">
              <input id="klienti-new-dic" className="rh-input block" value={form.dic} onChange={(e) => setForm((prev) => ({ ...prev, dic: e.target.value }))} data-testid="klienti-new-dic" />
            </FieldRow>
          </FieldGrid>
        )}
        <FieldRow
          label={t(form.kind === 'organization' ? 'webAdmin.klienti.companyNameLabel' : 'webAdmin.klienti.nameLabel', cs)}
          htmlFor="klienti-new-name"
        >
          <input
            id="klienti-new-name"
            required
            className="rh-input block"
            value={form.displayName}
            onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
            data-testid="klienti-new-name"
          />
        </FieldRow>
        <FieldGrid columns="1fr 1fr">
          <FieldRow label={t('webAdmin.klienti.phoneLabel', cs)} htmlFor="klienti-new-phone">
            <input id="klienti-new-phone" className="rh-input block" value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} data-testid="klienti-new-phone" />
          </FieldRow>
          <FieldRow label={t('webAdmin.klienti.emailLabel', cs)} htmlFor="klienti-new-email">
            <input id="klienti-new-email" type="email" className="rh-input block" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} data-testid="klienti-new-email" />
          </FieldRow>
        </FieldGrid>

        <FieldRow label={t('webAdmin.klienti.streetLabel', cs)} htmlFor="klienti-new-street">
          <input id="klienti-new-street" className="rh-input block" value={form.street} onChange={(e) => setForm((prev) => ({ ...prev, street: e.target.value }))} data-testid="klienti-new-street" />
        </FieldRow>
        <FieldGrid columns="1fr 1fr">
          <FieldRow label={t('webAdmin.klienti.cityLabel', cs)} htmlFor="klienti-new-city">
            <input id="klienti-new-city" className="rh-input block" value={form.city} onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))} data-testid="klienti-new-city" />
          </FieldRow>
          <FieldRow label={t('webAdmin.klienti.zipLabel', cs)} htmlFor="klienti-new-zip">
            <input id="klienti-new-zip" className="rh-input block" value={form.zip} onChange={(e) => setForm((prev) => ({ ...prev, zip: e.target.value }))} data-testid="klienti-new-zip" />
          </FieldRow>
        </FieldGrid>
      </FormSection>
        }
      />
    </form>
  );
}
